import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    deleteBattleAgent,
    disableBattleAgent,
    enableBattleAgent,
    getBattleAgents,
    getBattleAgentsSettings,
    updateBattleAgentsSettings,
} from '../Api';
import type {
    BattleAgentDto,
    BattleAgentMaxActiveScope,
    BattleAgentMaxActiveVisibility,
    BattleAgentsSettingsDto,
} from '../models/dtos/BattleAgentDto';

type PageMessage = { type: 'success' | 'error' | 'warning' | 'info'; text: string } | null;

const normalizeVisibility = (value: string | undefined): BattleAgentMaxActiveVisibility => {
    if (value === 'PUBLIC' || value === 'PRIVATE') return value;
    return 'BOTH';
};

const DEFAULT_SETTINGS: BattleAgentsSettingsDto = {
    enabled: false,
    intervalMs: 120_000,
    autoAcceptEnabled: false,
    autoAcceptMinutesBeforeKickoff: 8,
    maxActiveBattles: 0,
    maxActiveScope: 'BOTS',
    maxActiveVisibility: 'BOTH',
    maxCreatesPerTick: 1,
    maxAutoAcceptsPerTick: 5,
    activityBetsEnabled: false,
    activityBetsMaxPerTick: 1,
    activityBetsStakeMin: 500,
    activityBetsStakeMax: 2000,
    activityBetsCooldownHours: 12,
    activityBetsHoursAhead: 24,
};

export default function BattleAgentsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [savingSettings, setSavingSettings] = React.useState(false);
    const [message, setMessage] = React.useState<PageMessage>(null);
    const [agents, setAgents] = React.useState<BattleAgentDto[]>([]);
    const [settings, setSettings] = React.useState<BattleAgentsSettingsDto>(DEFAULT_SETTINGS);
    const [savedSettings, setSavedSettings] = React.useState<BattleAgentsSettingsDto>(DEFAULT_SETTINGS);

    const settingsDirty = React.useMemo(
        () => JSON.stringify(settings) !== JSON.stringify(savedSettings),
        [settings, savedSettings],
    );

    const loadData = React.useCallback(async () => {
        setLoading(true);
        setMessage(null);
        const [list, globalSettings] = await Promise.all([
            getBattleAgents(),
            getBattleAgentsSettings(),
        ]);
        if (list == null) {
            setAgents([]);
            setMessage({ type: 'error', text: 'Failed to load battle agents.' });
        } else {
            setAgents(list);
        }
        if (globalSettings == null) {
            setMessage(prev => prev ?? { type: 'error', text: 'Failed to load global battle agent settings.' });
        } else {
            const next = {
                ...DEFAULT_SETTINGS,
                ...globalSettings,
                maxActiveScope: (globalSettings.maxActiveScope === 'ALL' ? 'ALL' : 'BOTS') as BattleAgentMaxActiveScope,
                maxActiveVisibility: normalizeVisibility(globalSettings.maxActiveVisibility),
            };
            setSettings(next);
            setSavedSettings(next);
        }
        setLoading(false);
    }, []);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const patchSettings = <K extends keyof BattleAgentsSettingsDto>(key: K, value: BattleAgentsSettingsDto[K]) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const saveSettings = async () => {
        setSavingSettings(true);
        setMessage(null);
        const payload: BattleAgentsSettingsDto = {
            enabled: settings.enabled,
            intervalMs: Math.max(10_000, Math.floor(Number(settings.intervalMs) || 0)),
            autoAcceptEnabled: settings.autoAcceptEnabled,
            autoAcceptMinutesBeforeKickoff: Math.max(1, Math.floor(Number(settings.autoAcceptMinutesBeforeKickoff) || 0)),
            maxActiveBattles: Math.max(0, Math.floor(Number(settings.maxActiveBattles) || 0)),
            maxActiveScope: settings.maxActiveScope === 'ALL' ? 'ALL' : 'BOTS',
            maxActiveVisibility: normalizeVisibility(settings.maxActiveVisibility),
            maxCreatesPerTick: Math.max(1, Math.floor(Number(settings.maxCreatesPerTick) || 1)),
            maxAutoAcceptsPerTick: Math.max(0, Math.floor(Number(settings.maxAutoAcceptsPerTick) || 0)),
            activityBetsEnabled: settings.activityBetsEnabled,
            activityBetsMaxPerTick: Math.max(0, Math.floor(Number(settings.activityBetsMaxPerTick) || 0)),
            activityBetsStakeMin: Math.max(500, Math.floor(Number(settings.activityBetsStakeMin) || 500)),
            activityBetsStakeMax: Math.max(500, Math.floor(Number(settings.activityBetsStakeMax) || 500)),
            activityBetsCooldownHours: Math.max(1, Math.floor(Number(settings.activityBetsCooldownHours) || 12)),
            activityBetsHoursAhead: Math.max(1, Math.floor(Number(settings.activityBetsHoursAhead) || 24)),
        };
        const result = await updateBattleAgentsSettings(payload);
        setSavingSettings(false);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error });
            return;
        }
        const next = {
            ...DEFAULT_SETTINGS,
            ...result.data,
            maxActiveScope: (result.data.maxActiveScope === 'ALL' ? 'ALL' : 'BOTS') as BattleAgentMaxActiveScope,
            maxActiveVisibility: normalizeVisibility(result.data.maxActiveVisibility),
        };
        setSettings(next);
        setSavedSettings(next);
        setMessage({ type: 'success', text: 'Global battle agent settings saved.' });
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this battle agent?')) return;
        const result = await deleteBattleAgent(id);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error ?? 'Delete failed.' });
            return;
        }
        setMessage({ type: 'success', text: 'Agent deleted.' });
        await loadData();
    };

    const toggleEnabled = async (agent: BattleAgentDto) => {
        const result = agent.enabled
            ? await disableBattleAgent(agent.id)
            : await enableBattleAgent(agent.id);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error ?? 'Update failed.' });
            return;
        }
        await loadData();
    };

    const enabledAgentsWhileGlobalOff = !settings.enabled && agents.some(a => a.enabled);

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Typography variant="h4">Battle agents</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate('/battle-agents/bots')}>
                            Bot accounts
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/battle-agents/stats')}>
                            Stats
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/battle-agents/history')}>
                            History
                        </Button>
                        <Button variant="contained" onClick={() => navigate('/battle-agents/new')}>
                            New agent
                        </Button>
                    </Stack>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Automated bots that create public/private battles from selected dormant accounts.
                    Use global settings below for the kill switch and active-battle cap; enable/disable each agent individually in the list.
                    For PRIVATE agents with invite tags, Analytics saves a random invite pool on Save and refreshes it when the agent create interval elapses and/or every 50 successful creates; Crons picks randomly from that pool.
                </Typography>
                {enabledAgentsWhileGlobalOff && (
                    <Alert severity="warning">
                        Global battle agents are OFF. Enabled agents will not create battles until you turn global settings on and save.
                    </Alert>
                )}
                {settingsDirty && (
                    <Alert severity="info">
                        Global settings have unsaved changes. Click &quot;Save global settings&quot; to apply.
                    </Alert>
                )}
                {message && <Alert severity={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}

                {loading ? <CircularProgress /> : (
                    <>
                        <Card variant="outlined">
                            <CardContent>
                                <Stack spacing={2}>
                                    <Typography variant="h6">Global settings</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Default create cadence for agents with intervalSeconds = 0.
                                        Crons wakes every ~60s; each agent can override cadence on its edit page.
                                        Auto-accept (if enabled) runs on every 60s tick.
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.enabled}
                                                onChange={e => patchSettings('enabled', e.target.checked)}
                                            />
                                        }
                                        label="Battle agents enabled (global)"
                                    />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            type="number"
                                            label="Create every (seconds)"
                                            value={Math.round(settings.intervalMs / 1000)}
                                            onChange={e => patchSettings('intervalMs', Math.max(10, Number(e.target.value)) * 1000)}
                                            helperText={`Default for agents with Create every = 0. Min 10s. Current: every ${Math.round(settings.intervalMs / 1000)}s`}
                                            fullWidth
                                        />
                                        <TextField
                                            type="number"
                                            label="Max active battles"
                                            value={settings.maxActiveBattles}
                                            onChange={e => patchSettings('maxActiveBattles', Number(e.target.value))}
                                            helperText="0 = unlimited. Stops all agents from creating when reached (PENDING/ACCEPTED/STARTED)."
                                            fullWidth
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel id="max-active-scope-label">Count by creator</InputLabel>
                                            <Select
                                                labelId="max-active-scope-label"
                                                label="Count by creator"
                                                value={settings.maxActiveScope}
                                                onChange={e => patchSettings('maxActiveScope', e.target.value as BattleAgentMaxActiveScope)}
                                            >
                                                <MenuItem value="BOTS">BOTS (bot-created only)</MenuItem>
                                                <MenuItem value="ALL">ALL (every active battle)</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <InputLabel id="max-active-visibility-label">Count by visibility</InputLabel>
                                            <Select
                                                labelId="max-active-visibility-label"
                                                label="Count by visibility"
                                                value={settings.maxActiveVisibility}
                                                onChange={e => patchSettings('maxActiveVisibility', e.target.value as BattleAgentMaxActiveVisibility)}
                                            >
                                                <MenuItem value="BOTH">Both (public + private)</MenuItem>
                                                <MenuItem value="PUBLIC">Public only</MenuItem>
                                                <MenuItem value="PRIVATE">Private only</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.autoAcceptEnabled}
                                                onChange={e => patchSettings('autoAcceptEnabled', e.target.checked)}
                                            />
                                        }
                                        label="Auto-accept lonely public battles near kickoff"
                                    />
                                    <TextField
                                        type="number"
                                        label="Auto-accept minutes before kickoff"
                                        value={settings.autoAcceptMinutesBeforeKickoff}
                                        onChange={e => patchSettings('autoAcceptMinutesBeforeKickoff', Number(e.target.value))}
                                        helperText="Window before match start (1–120)"
                                        sx={{ maxWidth: 360 }}
                                    />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            type="number"
                                            label="Max creates per tick"
                                            value={settings.maxCreatesPerTick}
                                            onChange={e => patchSettings('maxCreatesPerTick', Number(e.target.value))}
                                            helperText="Agents processed per ~60s tick (1 = safest). Round-robin."
                                            fullWidth
                                        />
                                        <TextField
                                            type="number"
                                            label="Max auto-accepts per tick"
                                            value={settings.maxAutoAcceptsPerTick}
                                            onChange={e => patchSettings('maxAutoAcceptsPerTick', Number(e.target.value))}
                                            helperText="0 = unlimited. Caps accept load per tick."
                                            fullWidth
                                        />
                                    </Stack>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.activityBetsEnabled}
                                                onChange={e => patchSettings('activityBetsEnabled', e.target.checked)}
                                            />
                                        }
                                        label="Activity bets (occasional betlists for bot accounts)"
                                    />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            type="number"
                                            label="Activity bets max / tick"
                                            value={settings.activityBetsMaxPerTick}
                                            onChange={e => patchSettings('activityBetsMaxPerTick', Number(e.target.value))}
                                            fullWidth
                                        />
                                        <TextField
                                            type="number"
                                            label="Activity stake min"
                                            value={settings.activityBetsStakeMin}
                                            onChange={e => patchSettings('activityBetsStakeMin', Number(e.target.value))}
                                            fullWidth
                                        />
                                        <TextField
                                            type="number"
                                            label="Activity stake max"
                                            value={settings.activityBetsStakeMax}
                                            onChange={e => patchSettings('activityBetsStakeMax', Number(e.target.value))}
                                            fullWidth
                                        />
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            type="number"
                                            label="Activity cooldown hours / bot"
                                            value={settings.activityBetsCooldownHours}
                                            onChange={e => patchSettings('activityBetsCooldownHours', Number(e.target.value))}
                                            fullWidth
                                        />
                                        <TextField
                                            type="number"
                                            label="Activity match hours ahead"
                                            value={settings.activityBetsHoursAhead}
                                            onChange={e => patchSettings('activityBetsHoursAhead', Number(e.target.value))}
                                            fullWidth
                                        />
                                    </Stack>
                                </Stack>
                            </CardContent>
                            <CardActions>
                                <Button variant="contained" onClick={saveSettings} disabled={savingSettings || !settingsDirty}>
                                    {savingSettings ? 'Saving…' : 'Save global settings'}
                                </Button>
                            </CardActions>
                        </Card>

                        <Stack spacing={2}>
                            {agents.map(agent => {
                                const lastColor =
                                    agent.lastRunStatus === 'SUCCESS' ? 'success'
                                        : agent.lastRunStatus === 'FAILED' ? 'error'
                                            : agent.lastRunStatus === 'SKIPPED' ? 'warning'
                                                : 'default';
                                return (
                                    <Card key={agent.id}>
                                        <CardContent>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="subtitle1">{agent.name}</Typography>
                                                <Chip size="small" label={agent.slug} />
                                                <Chip size="small" color={agent.enabled ? 'success' : 'default'} label={agent.enabled ? 'Enabled' : 'Disabled'} />
                                                {!settings.enabled && agent.enabled && (
                                                    <Chip size="small" color="warning" label="Blocked by global OFF" />
                                                )}
                                                {agent.modes.map(mode => <Chip key={mode} size="small" label={mode} />)}
                                                <Chip size="small" label={`${agent.creatorUserIds.length} creators`} />
                                                <Chip size="small" label={`${agent.successLast24h}/${agent.runsLast24h} ok/24h`} />
                                                {agent.modes.includes('PRIVATE') && (
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            (agent.inviteTagSlugs?.length ?? 0) > 0
                                                                ? `tags→${agent.inviteUserIds?.length ?? 0} invitees`
                                                                : `${agent.inviteUserIds?.length ?? 0} manual invitees`
                                                        }
                                                    />
                                                )}
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                Stake {agent.stakeMin}–{agent.stakeMax}
                                                {' · '}
                                                every {agent.intervalSeconds > 0 ? `${agent.intervalSeconds}s` : 'global'}
                                                {' · '}
                                                max {agent.maxPerHour}/h · {agent.maxPerDay}/day
                                                {agent.modes.includes('PRIVATE') ? ` · invite tags: ${agent.inviteTagSlugs.join(', ') || '—'}` : ''}
                                            </Typography>
                                            {agent.lastRunStatus && (
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                                                    <Chip size="small" color={lastColor as 'default' | 'success' | 'error' | 'warning'} label={`Last: ${agent.lastRunStatus}`} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {agent.lastRunAt ? new Date(agent.lastRunAt).toLocaleString() : ''}
                                                        {agent.lastRunMessage ? ` · ${agent.lastRunMessage}` : ''}
                                                    </Typography>
                                                    {agent.lastRunChallengeId != null && (
                                                        <Button size="small" onClick={() => navigate('/battles/' + agent.lastRunChallengeId)}>
                                                            Open battle #{agent.lastRunChallengeId}
                                                        </Button>
                                                    )}
                                                </Stack>
                                            )}
                                        </CardContent>
                                        <CardActions>
                                            <Button size="small" onClick={() => navigate('/battle-agents/' + encodeURIComponent(agent.id) + '/edit')}>
                                                Edit
                                            </Button>
                                            <Button size="small" onClick={() => toggleEnabled(agent)}>
                                                {agent.enabled ? 'Disable' : 'Enable'}
                                            </Button>
                                            <Button size="small" color="error" onClick={() => handleDelete(agent.id)}>
                                                Delete
                                            </Button>
                                        </CardActions>
                                    </Card>
                                );
                            })}
                            {agents.length === 0 && <Typography color="text.secondary">No battle agents yet.</Typography>}
                        </Stack>
                    </>
                )}
            </Stack>
        </Box>
    );
}
